import { parseCurrencyInput } from '@/features/quotes/utils/group-life/format';
import { groupLifeService } from '@/services';
import type { GroupLifeQuoteDetails, GroupLifeTarifacaoLife } from '@/services/group-life';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { PmeLifeRow } from '../../components/pme/proposal.mock';

/** Target do estipulante master nos endpoints de vidas do Group Life. */
export const STIPULATOR_TARGET = '__STIPULATOR__';

export function groupLifeProposalLivesKey(policyId: string | undefined) {
  return ['group-life', 'proposal-lives', policyId] as const;
}

function normalizeLifeName(name?: string): string {
  const value = (name ?? '').trim();
  if (!value || value === '—') return '';
  return value.toUpperCase();
}

function buildFallbackSignature(params: {
  targetId: string;
  name?: string;
  birthDate?: string;
}): string {
  return `${params.targetId}::${normalizeLifeName(params.name)}::${params.birthDate ?? ''}`;
}

export type GroupLifeProposalLives = {
  lives: PmeLifeRow[];
  total: number;
  totalMemberCapital: number;
  isLoading: boolean;
};


export function useGroupLifeProposalLives(
  policyId: string | undefined,
  details: GroupLifeQuoteDetails | undefined,
): GroupLifeProposalLives {
  const queryClient = useQueryClient();
  const subIds = (details?.subStipulators ?? []).map(s => s.id);
  const queryKey = [...groupLifeProposalLivesKey(policyId), subIds] as const;

  const query = useQuery({
    queryKey,
    queryFn: async () => {
      if (!policyId) return [] as PmeLifeRow[];

      // Reusa IDs de fallback entre refetches para evitar "troca" de id na
      // tela quando a API devolve vidas sem id materializado.
      const previousLives = queryClient.getQueryData<PmeLifeRow[]>(queryKey) ?? [];
      const previousUsageBySignature = new Map<string, number>();
      const previousFallbackIdByOccurrence = new Map<string, string>();
      for (const life of previousLives) {
        if (life.memberId) continue;
        const signature = buildFallbackSignature({
          targetId: life.targetId ?? STIPULATOR_TARGET,
          name: life.name,
          birthDate: life.birthDate,
        });
        const occurrence = (previousUsageBySignature.get(signature) ?? 0) + 1;
        previousUsageBySignature.set(signature, occurrence);
        previousFallbackIdByOccurrence.set(`${signature}::${occurrence}`, life.id);
      }

      // 1. Busca membros pelo endpoint de membros
      const targets = [STIPULATOR_TARGET, ...subIds];
      const perTarget = await Promise.all(
        targets.map(async targetId => {
          const res = await groupLifeService.listMembersByTarget({
            quoteId: policyId,
            targetId,
          });
          const members = res.success && res.data ? res.data : [];
          return members.map(member => ({ member, targetId }));
        }),
      );
      const flatMembers = perTarget.flat();

      // 2. Enriquecimento pela tarifação (traz insurableGroup, capital e gender
      //    preenchidos) — SÓ quando alguma vida está com esses campos vazios.
      //    A página de tarifação carrega até 1.000 vidas; buscá-la em todo
      //    refetch (inclusive depois de cada inclusão/importação) era um dos
      //    custos maiores da tela, e no caso comum não muda nada.
      const tarifacaoByName = new Map<string, GroupLifeTarifacaoLife>();
      const needsEnrichment = flatMembers.some(
        ({ member }) =>
          !member.insurableGroup || !member.capital || !member.gender,
      );
      if (needsEnrichment) {
        try {
          const context = details?.statusPortal ? 'proposal' as const : 'quote' as const;
          const pricingRes = await groupLifeService.getTarifacaoPage({
            policyId,
            context,
            pageNo: 1,
            pageSize: 1000,
          });
          if (pricingRes.success && pricingRes.data?.data) {
            for (const life of pricingRes.data.data) {
              if (life.name) {
                tarifacaoByName.set(life.name.toUpperCase(), life);
              }
            }
          }
        } catch {
          // Se a tarifação falhar, segue sem enriquecer
        }
      }

      const currentUsageBySignature = new Map<string, number>();
      return flatMembers.map(({ member: m, targetId }, index) => {
        // Cruza com tarifação pelo nome para preencher campos faltantes
        const tarifacao = m.name
          ? tarifacaoByName.get(m.name.toUpperCase())
          : undefined;
        const memberId = m.id != null ? String(m.id) : undefined;
        const signature = buildFallbackSignature({
          targetId,
          name: m.name,
          birthDate: m.dateOfBirth,
        });
        const occurrence = (currentUsageBySignature.get(signature) ?? 0) + 1;
        currentUsageBySignature.set(signature, occurrence);
        const fallbackId = previousFallbackIdByOccurrence.get(
          `${signature}::${occurrence}`
        );

        const insurableGroup = m.insurableGroup || tarifacao?.insurableGroup || '';
        // Members devolve o salário. A tarifação já devolve o capital efetivo
        // (salário × quantidade de salários), então só o salário é multiplicado
        // na coluna.
        const memberCapital = m.capital?.trim() ?? '';
        const capital = memberCapital || tarifacao?.capital || '';
        const multiplySalary =
          !!memberCapital && details?.plan?.modality === 'MULTIPLE_SALARY';
        const gender = m.gender || tarifacao?.gender || '';

        return {
          id: memberId ?? fallbackId ?? `life-${signature}::${index}`,
          policyElementId: m.policyElementId,
          status: (m.awayFromWork
            ? 'inactive'
            : 'active') as PmeLifeRow['status'],
          insurableGroup: insurableGroup || '—',
          capital: capital || '—',
          multiplySalary,
          sex: gender || '—',
          birthDate: m.dateOfBirth || '—',
          name: m.name || '—',
          memberId,
          targetId,
          cpf: m.cpf || '',
          gender: (gender || '') as 'M' | 'F' | '',
          cids: m.cids ?? [],
          awayFromWork: !!m.awayFromWork,
        };
      });
    },
    enabled: !!policyId,
    // A lista é invalidada explicitamente por quem altera vidas (add/edit/
    // delete/importação). `staleTime: 0` + refetch no foco da janela refazia a
    // varredura completa (vidas por target + tarifação) a cada volta para a
    // aba, competindo com o recálculo em andamento.
    staleTime: 30_000,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    refetchOnReconnect: true,
  });

  const lives = query.data ?? [];
  const totalMemberCapital = lives.reduce(
    (acc, life) => acc + (parseCurrencyInput(life.capital) ?? 0),
    0,
  );

  return {
    lives,
    total: lives.length,
    totalMemberCapital,
    isLoading: query.isLoading,
  };
}
